// @flow

import { captureException } from 'shared/utils/errorTracking/errorTracking';
import { logger } from 'shared/utils/logger';

const errorInfo = {
  name: 'fetchWithTimeout',
  context: {
    severity: 'warning',
    tags: 'warning'
  }
};

export const fetchWithTimeoutAndAbort = (
  url: RequestInfo,
  params: RequestOptions = {},
  timeoutMs: number
): [Promise<any>, () => void] => {
  logger.log(`Making request to ${url.toString()}`);
  const controller = new AbortController();
  const abort = controller.abort.bind(controller);

  const response = fetch(url, {
    ...params,
    signal: controller.signal
  });

  const timeout = setTimeout(() => {
    captureException(
      new Error(
        `The network call to ${url.toString()} timed out after ${timeoutMs}ms`
      ),
      errorInfo
    );
    abort();
  }, timeoutMs);

  const augmentedResponse = response
    .then((res) => {
      if (!res.ok) {
        if (res.status >= 500 && res.status <= 599) {
          captureException(
            new Error(
              `The network call to ${url.toString()} failed with error ${
                res.status
              }`
            ),
            errorInfo
          );
        }
        throw res;
      }
      logger.log(`Received response from ${url.toString()}`);
      return res;
    })
    .finally(() => {
      clearTimeout(timeout);
    });

  return [augmentedResponse, abort];
};

export const fetchJsonWithTimeoutAndAbort = (
  url: RequestInfo,
  params: RequestOptions = {},
  timeoutMs: number
): [Promise<any>, () => void] => {
  const [response, abort] = fetchWithTimeoutAndAbort(url, params, timeoutMs);

  const json = response.then((res) => {
    if (!res.ok) throw res;

    return res.json();
  });

  return [json, abort];
};
